import { supabase } from '../lib/supabase';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface OfficeRecord {
  id: string;
  officeName: string;
  officeEmail: string;
  officePhone: string;
  area: string;
  city: string;
  state: string;
  address: string;
  latitude: number;
  longitude: number;
  allowedRadiusMeters: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AttendanceUser {
  id: string;
  uid?: string;
  displayName: string;
  email: string;
  state?: string;
  city?: string;
  location?: string;
}

export interface OfficeAssignmentRecord {
  id: string;
  officeId: string;
  userId: string;
  userUid?: string;
  assignedAt?: string;
  office?: Pick<OfficeRecord, 'id' | 'officeName' | 'city' | 'state'>;
  user?: AttendanceUser;
}

export interface AttendanceRecord {
  id: string;
  userId?: string | null;
  userUid?: string | null;
  attendanceDate: string;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  checkInLatitude?: number | null;
  checkInLongitude?: number | null;
  checkOutLatitude?: number | null;
  checkOutLongitude?: number | null;
  checkInDistanceMeters?: number | null;
  checkOutDistanceMeters?: number | null;
  checkInStatus?: string | null;
  checkOutStatus?: string | null;
  locationPermissionStatus?: string | null;
  isLate: boolean;
  isEarly: boolean;
  office?: Pick<OfficeRecord, 'id' | 'officeName' | 'city' | 'state' | 'allowedRadiusMeters'>;
  user?: AttendanceUser;
}

export interface AttendanceSummary {
  totalOffices: number;
  activeAssignments: number;
  todayPresent: number;
  todayDenied: number;
}

export interface AttendanceFilters {
  officeId?: string;
  userId?: string;
  status?: 'all' | 'success' | 'denied';
  fromDate?: string;
  toDate?: string;
}

export interface OfficeInput {
  officeName: string;
  officeEmail: string;
  officePhone: string;
  area: string;
  city: string;
  state: string;
  address: string;
  latitude: number;
  longitude: number;
  allowedRadiusMeters?: number;
  active?: boolean;
}

export interface CheckInEvaluation {
  distanceMeters: number;
  withinGeofence: boolean;
  isLate: boolean;
  isEarly: boolean;
  officeRadiusMeters: number;
}

const DEFAULT_RADIUS_METERS = 50;
const OFFICE_LATE_HOUR = 8;
const OFFICE_LATE_MINUTE = 45;
const OFFICE_EARLY_START_HOUR = 7;
const OFFICE_EARLY_START_MINUTE = 0;
const OFFICE_EARLY_END_HOUR = 8;
const OFFICE_EARLY_END_MINUTE = 30;

const startOfLocalDay = (value: Date) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const minutesFromDate = (value: Date) => value.getHours() * 60 + value.getMinutes();

const toRadians = (value: number) => (value * Math.PI) / 180;

const calculateHaversineMeters = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  const earthRadiusMeters = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  return earthRadiusMeters * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const normalizeAttendanceError = (error: any, fallback: string) => {
  const message = String(error?.message || fallback);
  const code = String(error?.code || '');

  if (message.includes("Could not find the table 'public.offices'")) {
    return 'Attendance setup is incomplete: the `offices` table is missing in Supabase.';
  }

  if (message.includes("Could not find the table 'public.office_user_assignments'")) {
    return 'Attendance setup is incomplete: the `office_user_assignments` table is missing in Supabase.';
  }

  if (message.includes("Could not find the table 'public.attendance_records'")) {
    return 'Attendance setup is incomplete: the `attendance_records` table is missing in Supabase.';
  }

  if (message.includes('user_uid')) {
    return 'Attendance schema is incomplete: add a `user_uid` text column for Firebase admin users.';
  }

  if (message.includes('attendance_date')) {
    return 'Attendance schema is incomplete: make sure `attendance_date` exists and is configured correctly.';
  }

  if (message.includes('users_1.') || code === '42703') {
    return 'Attendance report query does not match the current database schema. The user columns need alignment.';
  }

  if (code === '23502') {
    return 'Required attendance fields are missing in the database schema. Check nullable constraints for `user_id` and `user_uid`.';
  }

  if (code === '22P02') {
    return 'Attendance failed because a Firebase user ID was treated like a UUID. The table needs `user_uid` support.';
  }

  return message || fallback;
};

const mapOffice = (row: any): OfficeRecord => ({
  id: row.id,
  officeName: row.office_name || '',
  officeEmail: row.office_email || '',
  officePhone: row.office_phone || '',
  area: row.area || '',
  city: row.office_city || '',
  state: row.office_state || '',
  address: row.office_address || '',
  latitude: Number(row.office_latitude || 0),
  longitude: Number(row.office_longitude || 0),
  allowedRadiusMeters: Number(row.allowed_radius_meters || DEFAULT_RADIUS_METERS),
  active: row.active !== false,
  createdAt: row.created_at,
  updatedAt: row.updated_at
});

const mapUser = (row: any): AttendanceUser => ({
  id: row.id,
  uid: row.uid || undefined,
  displayName: row.display_name || row.email || 'Unnamed User',
  email: row.email || '',
  state: row.state || '',
  city: row.city || '',
  location: row.location || ''
});

const mapAssignment = (
  row: any,
  adminUidByDocId?: Map<string, string>
): OfficeAssignmentRecord => {
  const rawUserId = row.user_uid || row.user_id;
  const resolvedUserId = adminUidByDocId?.get(rawUserId) || rawUserId;

  return {
    id: row.id,
    officeId: row.office_id,
    userId: resolvedUserId,
    userUid: resolvedUserId,
  assignedAt: row.assigned_at,
  office: row.office
    ? {
        id: row.office.id,
        officeName: row.office.office_name || '',
        city: row.office.office_city || '',
        state: row.office.office_state || ''
      }
    : undefined,
  user: row.user ? mapUser(row.user) : undefined
  };
};

const mapAttendanceRecord = (row: any): AttendanceRecord => ({
  id: row.id,
  userId: row.user_id || null,
  userUid: row.user_uid || row.user?.uid || null,
  attendanceDate: row.attendance_date,
  checkInTime: row.check_in_time,
  checkOutTime: row.check_out_time,
  checkInLatitude: row.check_in_latitude != null ? Number(row.check_in_latitude) : null,
  checkInLongitude: row.check_in_longitude != null ? Number(row.check_in_longitude) : null,
  checkOutLatitude: row.check_out_latitude != null ? Number(row.check_out_latitude) : null,
  checkOutLongitude: row.check_out_longitude != null ? Number(row.check_out_longitude) : null,
  checkInDistanceMeters: row.check_in_distance_meters != null ? Number(row.check_in_distance_meters) : null,
  checkOutDistanceMeters: row.check_out_distance_meters != null ? Number(row.check_out_distance_meters) : null,
  checkInStatus: row.check_in_status || '',
  checkOutStatus: row.check_out_status || '',
  locationPermissionStatus: row.location_permission_status || '',
  isLate: Boolean(row.is_late),
  isEarly: Boolean(row.is_early),
  office: row.office
    ? {
        id: row.office.id,
        officeName: row.office.office_name || '',
        city: row.office.office_city || '',
        state: row.office.office_state || '',
        allowedRadiusMeters: Number(row.office.allowed_radius_meters || DEFAULT_RADIUS_METERS)
      }
    : undefined,
  user: row.user ? mapUser(row.user) : undefined
});

class AttendanceService {
  private async findAttendanceForDay(userId: string, attendanceDate: string) {
    const query = supabase
      .from('attendance_records')
      .select('id, user_id, user_uid, check_in_time, check_out_time, attendance_date')
      .eq('attendance_date', attendanceDate);

    const result = isUuid(userId)
      ? await query.eq('user_id', userId).maybeSingle()
      : await query.eq('user_uid', userId).maybeSingle();

    if (result.error) {
      throw new Error(normalizeAttendanceError(result.error, 'Failed to load attendance record.'));
    }

    return result.data;
  }

  evaluateCheckIn(
    office: Pick<OfficeRecord, 'latitude' | 'longitude' | 'allowedRadiusMeters'>,
    latitude: number,
    longitude: number,
    now: Date = new Date()
  ): CheckInEvaluation {
    const distanceMeters = calculateHaversineMeters(
      latitude,
      longitude,
      office.latitude,
      office.longitude
    );
    const currentMinutes = minutesFromDate(now);
    const lateMinutes = OFFICE_LATE_HOUR * 60 + OFFICE_LATE_MINUTE;
    const earlyStartMinutes = OFFICE_EARLY_START_HOUR * 60 + OFFICE_EARLY_START_MINUTE;
    const earlyEndMinutes = OFFICE_EARLY_END_HOUR * 60 + OFFICE_EARLY_END_MINUTE;
    const officeRadiusMeters = office.allowedRadiusMeters || DEFAULT_RADIUS_METERS;

    return {
      distanceMeters,
      withinGeofence: distanceMeters <= officeRadiusMeters,
      isLate: currentMinutes > lateMinutes,
      isEarly: currentMinutes >= earlyStartMinutes && currentMinutes <= earlyEndMinutes,
      officeRadiusMeters
    };
  }

  async getAttendanceSummary(): Promise<AttendanceSummary> {
    const today = startOfLocalDay(new Date()).toISOString().slice(0, 10);

    const [
      officesResult,
      assignmentsResult,
      presentResult,
      deniedResult
    ] = await Promise.all([
      supabase.from('offices').select('id', { count: 'exact', head: true }).eq('active', true),
      supabase.from('office_user_assignments').select('id', { count: 'exact', head: true }),
      supabase
        .from('attendance_records')
        .select('id', { count: 'exact', head: true })
        .eq('attendance_date', today)
        .eq('check_in_status', 'success'),
      supabase
        .from('attendance_records')
        .select('id', { count: 'exact', head: true })
        .eq('attendance_date', today)
        .eq('check_in_status', 'denied')
    ]);

    const firstError = [
      officesResult.error,
      assignmentsResult.error,
      presentResult.error,
      deniedResult.error
    ].find(Boolean);

    if (firstError) {
      throw new Error((firstError as any).message || 'Failed to load attendance summary.');
    }

    return {
      totalOffices: officesResult.count || 0,
      activeAssignments: assignmentsResult.count || 0,
      todayPresent: presentResult.count || 0,
      todayDenied: deniedResult.count || 0
    };
  }

  async getOffices(): Promise<OfficeRecord[]> {
    const { data, error } = await supabase
      .from('offices')
      .select('*')
      .order('office_name', { ascending: true });

    if (error) {
      throw new Error(normalizeAttendanceError(error, 'Failed to load offices.'));
    }

    return (data || []).map(mapOffice);
  }

  async saveOffice(input: OfficeInput, officeId?: string): Promise<OfficeRecord> {
    const payload = {
      office_name: input.officeName.trim(),
      office_email: input.officeEmail.trim().toLowerCase(),
      office_phone: input.officePhone.trim(),
      area: input.area.trim(),
      office_city: input.city.trim(),
      office_state: input.state.trim(),
      office_address: input.address.trim(),
      office_latitude: input.latitude,
      office_longitude: input.longitude,
      allowed_radius_meters: input.allowedRadiusMeters || DEFAULT_RADIUS_METERS,
      active: input.active !== false,
      updated_at: new Date().toISOString()
    };

    const query = officeId
      ? supabase.from('offices').update(payload).eq('id', officeId).select().single()
      : supabase
          .from('offices')
          .insert({
            ...payload,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

    const { data, error } = await query;

    if (error) {
      throw new Error(normalizeAttendanceError(error, 'Failed to save office.'));
    }

    return mapOffice(data);
  }

  async getAssignableUsers(): Promise<AttendanceUser[]> {
    if (db) {
      const snapshot = await getDocs(collection(db, 'admins'));
      const adminUsers = snapshot.docs
        .map((record) => {
          const data = record.data() as any;
          const resolvedUid = data.uid || record.id;
          return {
            id: resolvedUid,
            uid: resolvedUid,
            displayName: data.displayName || data.display_name || data.email || 'Unnamed User',
            email: data.email || '',
            state: Array.isArray(data.regions) ? data.regions.join(', ') : '',
            city: '',
            location: Array.isArray(data.regions) ? data.regions.join(', ') : ''
          } as AttendanceUser;
        })
        .filter((user) => Boolean(user.email))
        .sort((left, right) => left.displayName.localeCompare(right.displayName));

      if (adminUsers.length > 0) {
        return adminUsers;
      }
    }

    const { data, error } = await supabase
      .from('users')
      .select('id, uid, display_name, email, state, city, location')
      .order('display_name', { ascending: true });

    if (error) {
      throw new Error(normalizeAttendanceError(error, 'Failed to load users.'));
    }

    return (data || []).map(mapUser);
  }

  async getOfficeAssignments(): Promise<OfficeAssignmentRecord[]> {
    const { data, error } = await supabase
      .from('office_user_assignments')
      .select(`
        id,
        office_id,
        user_id,
        user_uid,
        assigned_at,
        office:offices(id, office_name, office_city, office_state),
        user:users(id, uid, display_name, email)
      `)
      .order('assigned_at', { ascending: false });

    if (error) {
      throw new Error(normalizeAttendanceError(error, 'Failed to load office assignments.'));
    }

    let adminUidByDocId: Map<string, string> | undefined;

    if (db) {
      try {
        const snapshot = await getDocs(collection(db, 'admins'));
        adminUidByDocId = new Map(
          snapshot.docs.map((record) => {
            const adminData = record.data() as any;
            return [record.id, adminData.uid || record.id];
          })
        );
      } catch (firebaseError) {
        // Non-super-admins may not be allowed to list all admin docs; keep raw ids in that case.
        console.warn('Unable to normalize office assignments from Firestore admins collection.', firebaseError);
      }
    }

    return (data || []).map((row) => mapAssignment(row, adminUidByDocId));
  }

  async assignUserToOffice(userId: string, officeId: string): Promise<void> {
    const existingAssignmentsQuery = supabase
      .from('office_user_assignments')
      .select('id, user_id, user_uid');

    const existingAssignmentsResult = isUuid(userId)
      ? await existingAssignmentsQuery.eq('user_id', userId)
      : await existingAssignmentsQuery.eq('user_uid', userId);

    if (existingAssignmentsResult.error) {
      throw new Error(normalizeAttendanceError(existingAssignmentsResult.error, 'Failed to load existing office assignment.'));
    }

    const existingAssignments = existingAssignmentsResult.data || [];
    if (existingAssignments.length > 0) {
      const { error: deleteError } = await supabase
        .from('office_user_assignments')
        .delete()
        .in('id', existingAssignments.map((assignment) => assignment.id));

      if (deleteError) {
        throw new Error(normalizeAttendanceError(deleteError, 'Failed to clear existing assignment.'));
      }
    }

    const basePayload = {
      office_id: officeId,
      assigned_at: new Date().toISOString()
    };

    let insertError: any = null;

    const firebaseAssignmentAttempt = await supabase
      .from('office_user_assignments')
      .insert({
        ...basePayload,
        user_uid: userId
      });

    insertError = firebaseAssignmentAttempt.error;

    if (insertError && String(insertError.message || '').includes("user_uid")) {
      const legacyAttempt = await supabase
        .from('office_user_assignments')
        .insert({
          ...basePayload,
          user_id: userId
        });
      insertError = legacyAttempt.error;
    }

    if (insertError) {
      throw new Error(normalizeAttendanceError(insertError, 'Failed to assign office.'));
    }
  }

  async getAttendanceRecords(filters: AttendanceFilters = {}): Promise<AttendanceRecord[]> {
    let query = supabase
      .from('attendance_records')
      .select(`
        id,
        user_id,
        user_uid,
        attendance_date,
        check_in_time,
        check_out_time,
        check_in_latitude,
        check_in_longitude,
        check_out_latitude,
        check_out_longitude,
        check_in_distance_meters,
        check_out_distance_meters,
        check_in_status,
        check_out_status,
        location_permission_status,
        is_late,
        is_early,
        office:offices(id, office_name, office_city, office_state, allowed_radius_meters),
        user:users(id, uid, display_name, email)
      `)
      .order('attendance_date', { ascending: false })
      .order('check_in_time', { ascending: false });

    if (filters.officeId) {
      query = query.eq('office_id', filters.officeId);
    }

    if (filters.userId) {
      query = isUuid(filters.userId)
        ? query.eq('user_id', filters.userId)
        : query.eq('user_uid', filters.userId);
    }

    if (filters.status && filters.status !== 'all') {
      query = query.eq('check_in_status', filters.status);
    }

    if (filters.fromDate) {
      query = query.gte('attendance_date', filters.fromDate);
    }

    if (filters.toDate) {
      query = query.lte('attendance_date', filters.toDate);
    }

    const { data, error } = await query.limit(250);

    if (error) {
      throw new Error(normalizeAttendanceError(error, 'Failed to load attendance records.'));
    }

    return (data || []).map(mapAttendanceRecord);
  }

  async recordCheckIn(input: {
    userId: string;
    officeId: string;
    latitude: number;
    longitude: number;
    locationPermissionStatus?: 'always' | 'while_in_use' | 'denied';
    source?: string;
    now?: Date;
  }): Promise<AttendanceRecord> {
    const [office] = await Promise.all([
      supabase.from('offices').select('*').eq('id', input.officeId).single()
    ]);

    if (office.error || !office.data) {
      throw new Error(normalizeAttendanceError(office.error, 'Office not found.'));
    }

    const officeRecord = mapOffice(office.data);
    const evaluation = this.evaluateCheckIn(officeRecord, input.latitude, input.longitude, input.now);
    const effectiveNow = input.now || new Date();
    const attendanceDate = startOfLocalDay(effectiveNow).toISOString().slice(0, 10);

    const payload: any = {
      office_id: input.officeId,
      attendance_date: attendanceDate,
      check_in_time: effectiveNow.toISOString(),
      check_in_latitude: input.latitude,
      check_in_longitude: input.longitude,
      check_in_distance_meters: Number(evaluation.distanceMeters.toFixed(2)),
      check_in_status: evaluation.withinGeofence ? 'success' : 'denied',
      check_out_status: null,
      location_permission_status: input.locationPermissionStatus || 'while_in_use',
      is_late: evaluation.isLate,
      is_early: evaluation.isEarly,
      check_in_source: input.source || 'mobile'
    };

    if (isUuid(input.userId)) {
      payload.user_id = input.userId;
    } else {
      payload.user_id = null;
      payload.user_uid = input.userId;
    }

    const existingRecord = await this.findAttendanceForDay(input.userId, attendanceDate);

    if (existingRecord?.check_in_time) {
      throw new Error('You have already checked in today.');
    }

    const writeQuery = existingRecord?.id
      ? supabase.from('attendance_records').update(payload).eq('id', existingRecord.id)
      : supabase
          .from('attendance_records')
          .insert(payload);

    const { data, error } = await writeQuery
      .select(`
        id,
        user_id,
        user_uid,
        attendance_date,
        check_in_time,
        check_out_time,
        check_in_latitude,
        check_in_longitude,
        check_out_latitude,
        check_out_longitude,
        check_in_distance_meters,
        check_out_distance_meters,
        check_in_status,
        check_out_status,
        location_permission_status,
        is_late,
        is_early,
        office:offices(id, office_name, office_city, office_state, allowed_radius_meters),
        user:users(id, uid, display_name, email)
      `)
      .single();

    if (error) {
      throw new Error(normalizeAttendanceError(error, 'Failed to record check-in.'));
    }

    return mapAttendanceRecord(data);
  }

  async recordCheckOut(input: {
    userId: string;
    officeId: string;
    latitude: number;
    longitude: number;
    source?: string;
    now?: Date;
  }): Promise<AttendanceRecord> {
    const effectiveNow = input.now || new Date();
    const attendanceDate = startOfLocalDay(effectiveNow).toISOString().slice(0, 10);

    const officeResult = await supabase.from('offices').select('*').eq('id', input.officeId).single();
    if (officeResult.error || !officeResult.data) {
      throw new Error(normalizeAttendanceError(officeResult.error, 'Office not found.'));
    }

    const officeRecord = mapOffice(officeResult.data);
    const evaluation = this.evaluateCheckIn(officeRecord, input.latitude, input.longitude, effectiveNow);

    const existingRecord = await this.findAttendanceForDay(input.userId, attendanceDate);

    if (!existingRecord?.id) {
      throw new Error('No check-in record found for today.');
    }

    if (!existingRecord.check_in_time) {
      throw new Error('You need to check in first before checking out.');
    }

    if (existingRecord.check_out_time) {
      throw new Error('You have already checked out today.');
    }

    const { data, error } = await supabase
      .from('attendance_records')
      .update({
        office_id: input.officeId,
        check_out_time: effectiveNow.toISOString(),
        check_out_latitude: input.latitude,
        check_out_longitude: input.longitude,
        check_out_distance_meters: Number(evaluation.distanceMeters.toFixed(2)),
        check_out_status: evaluation.withinGeofence ? 'success' : 'denied',
        check_out_source: input.source || 'mobile'
      })
      .eq('id', existingRecord.id)
      .select(`
        id,
        user_id,
        user_uid,
        attendance_date,
        check_in_time,
        check_out_time,
        check_in_latitude,
        check_in_longitude,
        check_out_latitude,
        check_out_longitude,
        check_in_distance_meters,
        check_out_distance_meters,
        check_in_status,
        check_out_status,
        location_permission_status,
        is_late,
        is_early,
        office:offices(id, office_name, office_city, office_state, allowed_radius_meters),
        user:users(id, uid, display_name, email)
      `)
      .single();

    if (error) {
      throw new Error(normalizeAttendanceError(error, 'Failed to record check-out.'));
    }

    return mapAttendanceRecord(data);
  }
}

export const attendanceService = new AttendanceService();
export default attendanceService;
